// src/components/user/PaymentMethodForm.jsx

import React, { useState, useEffect } from "react";
import api from "../../api/api";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  ElementsConsumer,
} from "@stripe/react-stripe-js";
import {
  FaCreditCard,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;
const allowMockPaymentEntry = process.env.NODE_ENV !== "production";

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#1f2937",
      "::placeholder": {
        color: "#9ca3af",
      },
    },
    invalid: {
      color: "#dc2626",
    },
  },
};

const PaymentMethodFormContent = ({
  onSave,
  onCancel,
  methodToEdit,
  stripe,
  elements,
}) => {
  const { t } = useTranslation();
  const isEditing = !!methodToEdit;

  const [formData, setFormData] = useState({
    cardholder_name: "",
    is_default: false,
    mock_last4: "4242",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isEditing) {
      setFormData({
        cardholder_name: methodToEdit.cardholder_name || "",
        is_default: methodToEdit.is_default || false,
        mock_last4: methodToEdit.last4 || "4242",
      });
    }
  }, [methodToEdit, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (isEditing) {
        const updateFields = {
          cardholder_name: formData.cardholder_name,
          is_default: formData.is_default,
        };

        response = await api.put(
          `/user/payment-methods/${methodToEdit.payment_method_id}`,
          updateFields,
        );
        setSuccess(t("payment.form.successEdit"));
      } else {
        if (!stripePromise && allowMockPaymentEntry) {
          const normalizedLast4 = String(formData.mock_last4 || "")
            .replace(/\D/g, "")
            .slice(-4)
            .padStart(4, "0");

          const newMethodData = {
            psp_customer_token: `mock_pm_${Date.now()}`,
            psp_name: "mock",
            method_type: "card",
            card_brand: "MockCard",
            last4: normalizedLast4,
            exp_month: 12,
            exp_year: 2030,
            cardholder_name: formData.cardholder_name,
            is_default: formData.is_default,
          };

          response = await api.post("/user/payment-methods", newMethodData);
          setSuccess(t("payment.form.successAdd"));
          setTimeout(() => onSave(response.data), 1500);
          return;
        }

        if (!stripe || !elements) {
          setError(
            t(
              "payment.form.stripeNotReady",
              "Secure card form is not ready. Please refresh and try again.",
            ),
          );
          setLoading(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          setError(
            t(
              "payment.form.cardElementMissing",
              "Card input is missing. Please refresh and try again.",
            ),
          );
          setLoading(false);
          return;
        }

        const { error: stripeError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: formData.cardholder_name || undefined,
            },
          });

        if (stripeError) {
          setError(stripeError.message || t("payment.form.error"));
          setLoading(false);
          return;
        }

        const newMethodData = {
          psp_customer_token: paymentMethod.id,
          psp_name: "stripe",
          method_type: "card",
          cardholder_name: formData.cardholder_name,
          is_default: formData.is_default,
        };

        response = await api.post("/user/payment-methods", newMethodData);
        setSuccess(t("payment.form.successAdd"));
      }

      // Wait a moment for success message, then notify parent to refresh
      setTimeout(() => onSave(response.data), 1500);
    } catch (err) {
      console.error("Error saving payment method:", err);
      // Check for validation errors from the backend (as seen in the controller)
      const message = err.response?.data?.message || t("payment.form.error");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const renderCardDisplayFields = () => {
    if (isEditing) {
      return (
        <div className="md:col-span-2 space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="font-semibold text-gray-800 flex items-center">
            <FaCreditCard className="mr-2 text-blue-500" />{" "}
            {t("payment.form.cardDetails")}
          </p>
          <div className="grid grid-cols-2 text-sm text-gray-600">
            <span className="font-medium">{t("payment.form.cardLabel")}</span>
            <span>
              {t("payment.form.cardEndingIn", "{{brand}} ending in {{last4}}", {
                brand: methodToEdit?.card_brand || "Card",
                last4: methodToEdit?.last4 || "XXXX",
              })}
            </span>
            <span className="font-medium">{t("payment.form.expires")}</span>
            <span>
              {String(methodToEdit?.exp_month || "--").padStart(2, "0")}/
              {methodToEdit?.exp_year || "----"}
            </span>
          </div>
          <p className="text-xs text-red-500">
            {t("payment.form.changeCardHint")}
          </p>
        </div>
      );
    }

    if (!stripePromise) {
      return (
        <>
          <div className="md:col-span-2 mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <p className="text-sm text-red-800 font-semibold flex items-center">
              <FaExclamationTriangle className="mr-2 w-4 h-4" />
              {t(
                "payment.form.stripePublishableKeyMissing",
                "Stripe publishable key is missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY.",
              )}
            </p>
            {allowMockPaymentEntry && (
              <p className="mt-1 text-xs text-red-700">
                {t(
                  "payment.form.devMockFallback",
                  "Development fallback is enabled: a mock card will be saved for local testing.",
                )}
              </p>
            )}
          </div>

          {allowMockPaymentEntry && (
            <div className="md:col-span-2 mb-4">
              <label className="block text-gray-700 font-medium">
                {t("payment.form.mockLast4", "Card Last 4 (Mock)")}
              </label>
              <input
                type="text"
                name="mock_last4"
                value={formData.mock_last4}
                onChange={handleChange}
                className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                maxLength={4}
                inputMode="numeric"
              />
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <div className="md:col-span-2 mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
          <p className="text-sm text-green-800 font-semibold flex items-center">
            <FaLock className="mr-2 w-4 h-4" />
            {t(
              "payment.form.secureStripeEntry",
              "Your card details are collected securely by Stripe.",
            )}
          </p>
        </div>

        <div className="md:col-span-2 mb-4">
          <label className="block text-gray-700 font-medium">
            {t("payment.form.cardDetails", "Card Details")}{" "}
            <span className="text-xs text-gray-500">
              ({t("common.required")})
            </span>
          </label>
          <div className="mt-1 p-3 w-full border border-gray-300 rounded-md bg-white">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-xl max-w-xl mx-auto mt-8 border-t-4 border-blue-500">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
        <FaCreditCard className="mr-3 text-blue-600" />
        {isEditing ? t("payment.form.editTitle") : t("payment.form.addTitle")}
      </h2>
      <p className="text-sm text-gray-500 mb-6 flex items-center">
        <FaLock className="mr-1 text-green-500" />{" "}
        {t("payment.form.secureNote")}
      </p>

      <form onSubmit={handleSubmit}>
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4 flex items-center">
            <FaExclamationTriangle className="mr-2 w-4 h-4" /> {error}
          </div>
        )}
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 text-green-700 p-3 rounded-md mb-4 flex items-center">
            <FaCheckCircle className="mr-2 w-4 h-4" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Cardholder Name (Always editable for display) */}
          <div className="md:col-span-2 mb-4">
            <label className="block text-gray-700 font-medium">
              {t("payment.form.cardholderName")}
            </label>
            <input
              type="text"
              name="cardholder_name"
              value={formData.cardholder_name}
              onChange={handleChange}
              className="mt-1 p-3 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Render secure/PSP fields or display fields based on mode */}
          {renderCardDisplayFields()}

          {/* Is Default Checkbox (Always editable) */}
          <div className="md:col-span-2 mb-4">
            <label className="flex items-center space-x-2 text-gray-700 font-medium cursor-pointer">
              <input
                type="checkbox"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>{t("payment.form.makeDefault")}</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
            disabled={loading}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300"
            disabled={loading}
          >
            {loading
              ? t("common.saving")
              : isEditing
                ? t("payment.form.update")
                : t("payment.form.save")}
          </button>
        </div>
      </form>
    </div>
  );
};

const PaymentMethodForm = (props) => {
  if (props.methodToEdit || !stripePromise) {
    return (
      <PaymentMethodFormContent {...props} stripe={null} elements={null} />
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <PaymentMethodFormContent
            {...props}
            stripe={stripe}
            elements={elements}
          />
        )}
      </ElementsConsumer>
    </Elements>
  );
};

export default PaymentMethodForm;
